import React, { useState } from 'react';
import classNames from 'classnames';

import { formatCurrency } from '~libs/dataUtils';

import Icon, { faChevronDown, faChevronRight } from '~components/icon';
import { TableView } from '~components/table-view-v3';

import { ReconClient } from '~support-at-home/models';

import { columns } from '~support-at-home/forms/reconciliations/clientSummaryList';
import { amendmentColumns } from '~support-at-home/forms/reconciliations/amendmentSummaryList';

import StatusTag from './StatusTag';

interface ComponentProps {
  client: ReconClient;
}

const ClientCard: React.FC<ComponentProps> = ({ client }) => {
  const {
    client_id,
    full_name,
    status,
    total_gov_contribution_amount,
    total_co_contribution_amount,
    total_claimed,
    total_paid,
    items,
    currentItems,
    amendmentGroups,
    adjustmentGroups,
  } = client;
  const [open, setOpen] = useState(false);

  const currentCount = (currentItems ?? items).length;
  const amendmentCount = amendmentGroups?.length ?? 0;
  const adjustmentCount = adjustmentGroups?.length ?? 0;

  const pluralize = (count: number, singular: string) => `${count} ${singular}${count === 1 ? '' : 's'}`;

  return (
    <div className="border rounded bg-white shadow-sm mb-3" data-cy={`recon-client-card-${client_id}`}>
      <div className="d-flex align-items-center p-3 client-card-header" onClick={() => setOpen(!open)}>
        {/* Expand Icon */}
        <Icon icon={open ? faChevronDown : faChevronRight} className="me-3 text-secondary" />

        {/* Client Name */}
        <div className="fw-semibold">{full_name}</div>

        {/* Status */}
        <StatusTag status={status} />

        {/* Service count */}
        <div className="text-muted ms-2" data-cy={`recon-client-counts-${client_id}`}>
          {pluralize(currentCount, 'service')}
          {amendmentCount > 0 && `, ${pluralize(amendmentCount, 'amendment')}`}
          {adjustmentCount > 0 && `, ${pluralize(adjustmentCount, 'adjustment')}`}
        </div>

        {/* Spacing */}
        <div className="flex-grow-1" />

        {/* Claimed / Paid */}
        <div className="d-flex gap-4">
          <div>Claimed: {formatCurrency(total_claimed)}</div>
          <div>Govt. Contribution: {formatCurrency(total_gov_contribution_amount)}</div>
          <div>Co-Contribution: {formatCurrency(total_co_contribution_amount)}</div>
          <div
            className={classNames({
              'text-danger': total_paid < total_claimed,
            })}
          >
            Paid: {formatCurrency(total_paid)}
          </div>
        </div>
      </div>

      {/* Collapsed Content */}
      {open && (
        <div className="px-3 pb-3">
          {/* Current Services */}
          <h6 className="text-muted mb-2">Current Services</h6>
          <TableView
            id={`reconciliation-table-client-${client_id}`}
            columns={columns}
            data={currentItems ?? items}
            readonly
            striped
          />

          {/* Amendments */}
          {amendmentCount > 0 && (
            <div className="mt-3 pt-3 border-top">
              <h6 className="text-muted mb-2">Amendments</h6>
              <TableView
                id={`reconciliation-amendments-client-${client_id}`}
                columns={amendmentColumns}
                data={amendmentGroups ?? []}
                readonly
                striped
              />
            </div>
          )}

          {/* Government Adjustments */}
          {adjustmentCount > 0 && (
            <div className="mt-3 pt-3 border-top">
              <h6 className="text-muted mb-2">Government Adjustments</h6>
              <TableView
                id={`reconciliation-adjustments-client-${client_id}`}
                columns={amendmentColumns}
                data={adjustmentGroups ?? []}
                readonly
                striped
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ClientCard;
